import { useLocation, useNavigate } from "react-router-dom";
import { Box, Tab, Tabs } from "@mui/material";
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import BarChartIcon from '@mui/icons-material/BarChart';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import { ACCENTS, COLORS, SHADOW } from './emsTheme';
import { isEscalationAdmin } from './emsAccess';

const TABS = [
    { label: 'Tickets', path: '/tools/escalation-v2', icon: <ConfirmationNumberIcon sx={{ fontSize: 18 }} /> },
    { label: 'Analytics', path: '/tools/escalation-v2/analytics', icon: <BarChartIcon sx={{ fontSize: 18 }} /> },
    { label: 'Categories', path: '/tools/escalation-v2/categories', icon: <CategoryOutlinedIcon sx={{ fontSize: 18 }} />, adminOnly: true },
    { label: 'Auto Tagging & Email Config', path: '/tools/escalation-v2/settings', icon: <MailOutlineIcon sx={{ fontSize: 18 }} />, adminOnly: true },
    { label: 'Report Schedules', path: '/tools/escalation-v2/reports', icon: <ScheduleSendOutlinedIcon sx={{ fontSize: 18 }} />, adminOnly: true },
];

/** Tab bar for the Escalation Management V2 pages — mirrors the
 *  InterventionNav pattern (src/app/pages/intervention-analytics): each tab is a real route,
 *  not local component state, so either page is bookmarkable/shareable on its own.
 *
 *  Exact-path match rather than startsWith, deliberately: '/tools/escalation-v2' is a literal
 *  prefix of both '/tools/escalation-v2/analytics' and the ticket-detail route
 *  '/tools/escalation-v2/view', so startsWith would wrongly light up "Tickets" while on
 *  Analytics too. The detail view isn't one of these two tabs; it falls back to Tickets.
 *  The settings tabs show only to ESCALATION_ADMIN holders. */
const EscalationV2Nav = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const tabs = isEscalationAdmin() ? TABS : TABS.filter((t) => !t.adminOnly);
    const matched = tabs.findIndex((t, i) => i > 0 && location.pathname.startsWith(t.path));
    const activeIdx = matched > 0 ? matched : 0;

    return (
        <Box sx={{ mb: 2, bgcolor: '#fff', border: `1px solid ${COLORS.grid}`, borderRadius: '12px', boxShadow: SHADOW, px: 0.5 }}>
            <Tabs
                variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                value={activeIdx}
                onChange={(_, idx) => navigate(tabs[idx].path)}
                sx={{
                    minHeight: 48,
                    '& .MuiTab-root': { minHeight: 48, py: 1, px: 2, textTransform: 'none', fontWeight: 700, fontSize: 14, color: COLORS.ink3, transition: 'color 150ms' },
                    '& .MuiTab-root:hover': { color: COLORS.purple },
                    '& .MuiTab-root.Mui-selected': { color: COLORS.purple },
                    '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', background: ACCENTS.purple.grad },
                }}
            >
                {tabs.map((t) => (
                    <Tab key={t.path} label={t.label} icon={t.icon} iconPosition="start" />
                ))}
            </Tabs>
        </Box>
    );
};

export default EscalationV2Nav;
